import { useMemo, useState } from "react";
import { ScrollText } from "lucide-react";
import "./BondYieldCalculator.css";

const formatINR = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Math.round(n));

/** Approximate YTM: (C + (F-P)/n) / ((F+P)/2) * 100 */
function approxYtm(face, price, couponPct, years) {
  if (years <= 0 || face <= 0 || price <= 0) return 0;
  const C = (couponPct / 100) * face;
  return ((C + (face - price) / years) / ((face + price) / 2)) * 100;
}

export default function BondYieldCalculator() {
  const [face, setFace] = useState(1000);
  const [price, setPrice] = useState(950);
  const [coupon, setCoupon] = useState(7);
  const [years, setYears] = useState(5);

  const { ytm, annualCoupon, currentYield } = useMemo(() => {
    const y = approxYtm(face, price, coupon, years);
    const ac = (coupon / 100) * face;
    const cy = price > 0 ? (ac / price) * 100 : 0;
    return { ytm: y, annualCoupon: ac, currentYield: cy };
  }, [face, price, coupon, years]);

  return (
    <section className="calc-card" id="bond-yield">
      <div className="calc-card__head">
        <div className="calc-card__icon">
          <ScrollText size={22} />
        </div>
        <div>
          <h2>Bond Yield Calculator</h2>
          <p>Approximate yield to maturity (YTM) for plain vanilla bonds.</p>
        </div>
      </div>

      <div className="calc-layout">
        <div>
          <div className="calc-field">
            <div className="calc-field__top">
              <label htmlFor="bond-face">Face value</label>
              <input
                id="bond-face"
                className="calc-field__input"
                type="number"
                min={100}
                max={1000000}
                step={100}
                value={face}
                onChange={(e) => setFace(Number(e.target.value) || 0)}
              />
            </div>
            <input
              className="calc-range"
              type="range"
              min={100}
              max={10000}
              step={100}
              value={Math.min(Math.max(face, 100), 10000)}
              onChange={(e) => setFace(Number(e.target.value))}
            />
          </div>
          <div className="calc-field">
            <div className="calc-field__top">
              <label htmlFor="bond-price">Market price</label>
              <input
                id="bond-price"
                className="calc-field__input"
                type="number"
                min={50}
                max={1000000}
                step={10}
                value={price}
                onChange={(e) => setPrice(Number(e.target.value) || 0)}
              />
            </div>
            <input
              className="calc-range"
              type="range"
              min={50}
              max={15000}
              step={10}
              value={Math.min(Math.max(price, 50), 15000)}
              onChange={(e) => setPrice(Number(e.target.value))}
            />
          </div>
          <div className="calc-field">
            <div className="calc-field__top">
              <label htmlFor="bond-coupon">Coupon rate (% p.a.)</label>
              <input
                id="bond-coupon"
                className="calc-field__input"
                type="number"
                min={0}
                max={20}
                step={0.1}
                value={coupon}
                onChange={(e) => setCoupon(Number(e.target.value) || 0)}
              />
            </div>
            <input
              className="calc-range"
              type="range"
              min={0}
              max={20}
              step={0.1}
              value={coupon}
              onChange={(e) => setCoupon(Number(e.target.value))}
            />
          </div>
          <div className="calc-field">
            <div className="calc-field__top">
              <label htmlFor="bond-y">Years to maturity</label>
              <input
                id="bond-y"
                className="calc-field__input"
                type="number"
                min={1}
                max={40}
                value={years}
                onChange={(e) => setYears(Number(e.target.value) || 1)}
              />
            </div>
            <input
              className="calc-range"
              type="range"
              min={1}
              max={40}
              value={years}
              onChange={(e) => setYears(Number(e.target.value))}
            />
          </div>
        </div>

        <div className="calc-results">
          <div className="calc-result calc-result--accent">
            <span>Approx. YTM</span>
            <strong>{ytm.toFixed(2)}%</strong>
          </div>
          <div className="calc-result">
            <span>Current yield</span>
            <strong>{currentYield.toFixed(2)}%</strong>
          </div>
          <div className="calc-result">
            <span>Annual coupon</span>
            <strong>{formatINR(annualCoupon)}</strong>
          </div>
          <p className="calc-note">
            Approx. YTM ≈ (C + (F−P)/n) / ((F+P)/2) × 100
          </p>
        </div>
      </div>
    </section>
  );
}
